import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { listMedia, MediaItem } from '../lib/media';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (item: MediaItem) => void;
};

const MediaPickerModal: React.FC<Props> = ({ open, onClose, onSelect }) => {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isImage = (it: MediaItem | null | undefined) => !!it && (
    (it.mime || '').startsWith('image/') ||
    ['.jpg','.jpeg','.png','.gif','.webp','.svg','.bmp','.tiff','.ico','.heif','.heic']
      .some(ext => (it.name || '').toLowerCase().endsWith(ext))
  );

  useEffect(() => {
    if (!open) return;
    const go = async () => {
      setLoading(true); setError(null);
      try {
        const list = await listMedia();
        // Only keep images in the picker
        const onlyImages = list.filter(isImage).sort((a,b)=>b.mtime-a.mtime);
        setItems(onlyImages);
      } catch (e: any) {
        setError(e?.message || 'Failed to load media');
      } finally { setLoading(false); }
    };
    go();
  }, [open]);

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40" onMouseDown={onClose}>
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-4xl max-h-[80vh] overflow-hidden" onMouseDown={e=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-blue-50">
              <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 15l4-4 4 4 5-5 4 4"/></svg>
            </div>
            <div className="font-semibold text-gray-900">Choose Image</div>
          </div>
          <button className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-gray-100" onClick={onClose} aria-label="Close">
            <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="p-5 overflow-auto" style={{ maxHeight: 'calc(80vh - 64px)' }}>
          {error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>
          )}
          {loading ? (
            <div className="text-gray-600">Loading media…</div>
          ) : items.length === 0 ? (
            <div className="text-gray-600">No media found. Upload images in the Media Gallery.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {items.map((it) => (
                <button key={it.name} onClick={() => onSelect(it)} className="group aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-100 hover:ring-2 hover:ring-blue-300">
                  <img src={it.url} alt={it.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default MediaPickerModal;
